import React, { useMemo, useState } from 'react';
import { Card, Table } from 'react-bootstrap';
import useReservationTableDataV4 from '../../../services/statistic-data/hooks/useReservationTableV4'; // Sesuaikan path jika perlu
import ReservationTableSkeleton from './ReservationTableSkeleton';
import { formatPrice } from '../../../shared/utils/formatPrice';

// Menentukan tipe data untuk satu baris data, agar bisa digunakan untuk sorting key
type ReservationDataRow = {
    name: string;
    nb_reservations: number;
    nb_nights: string;
    avg_length_of_stay: string;
    total_earning: string;
    avg_earning: string;
    percentage: string;
};

// Menentukan tipe untuk kunci yang bisa di-sort
type SortableKeys = keyof ReservationDataRow;

interface ReservationTableV4Props {
    property_id: string;
    start_date: string;
    end_date: string;
}

const ReservationTableV4Component: React.FC<ReservationTableV4Props> = ({ property_id, start_date, end_date }) => {
    const { data: tableData, isFetching } = useReservationTableDataV4({
        filters: { property_id, start_date, end_date },
    });

    // State untuk menyimpan konfigurasi sorting saat ini
    const [sortConfig, setSortConfig] = useState<{ key: SortableKeys; direction: 'ascending' | 'descending' } | null>(null);

    // Gunakan useMemo untuk mengurutkan data hanya saat data atau konfigurasi sort berubah
    const sortedData = useMemo(() => {
        if (!tableData?.data) return [];

        let sortableItems = [...tableData.data]; // Buat salinan data agar tidak mengubah data asli

        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const valA = a[sortConfig.key];
                const valB = b[sortConfig.key];

                // Lakukan konversi ke angka untuk kolom yang seharusnya numerik tapi bertipe string
                const numericKeys: SortableKeys[] = ['nb_nights', 'avg_length_of_stay', 'total_earning', 'avg_earning', 'percentage'];
                if (numericKeys.includes(sortConfig.key)) {
                    const numA = parseFloat(String(valA));
                    const numB = parseFloat(String(valB));
                    if (numA < numB) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if (numA > numB) return sortConfig.direction === 'ascending' ? 1 : -1;
                    return 0;
                }

                // Sorting untuk string (misal: nama)
                if (typeof valA === 'string' && typeof valB === 'string') {
                    return sortConfig.direction === 'ascending' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                }

                // Fallback untuk tipe data lain (misal: nb_reservations yang sudah number)
                if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [tableData?.data, sortConfig]);


    // Fungsi untuk menangani klik pada header tabel untuk sorting
    const requestSort = (key: SortableKeys) => {
        let direction: 'ascending' | 'descending' = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // Fungsi untuk mendapatkan ikon indikator sort
    const getSortIndicator = (key: SortableKeys) => {
        if (!sortConfig || sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
    };

    if (isFetching) {
        return <ReservationTableSkeleton />;
    }

    if (!tableData || tableData.data.length === 0) {
        return (
            <Card className="mt-4">
                <Card.Body className="text-center">No reservation data available for this period.</Card.Body>
            </Card>
        );
    }

    const { total } = tableData;

    return (
        <Card className="mt-4">
            <Card.Header>
                <Card.Title>
                    <h3 className="mb-0">Reservations by Source</h3>
                </Card.Title>
            </Card.Header>
            <Card.Body>
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            {/* Tambahkan onClick dan indikator pada setiap header yang bisa di-sort */}
                            <th onClick={() => requestSort('name')} style={{ cursor: 'pointer' }}>
                                Source{getSortIndicator('name')}
                            </th>
                            <th onClick={() => requestSort('nb_reservations')} style={{ cursor: 'pointer' }}>
                                Reservations{getSortIndicator('nb_reservations')}
                            </th>
                            <th onClick={() => requestSort('nb_nights')} style={{ cursor: 'pointer' }}>
                                Nights{getSortIndicator('nb_nights')}
                            </th>
                            <th onClick={() => requestSort('avg_length_of_stay')} style={{ cursor: 'pointer' }}>
                                Avg. Stay{getSortIndicator('avg_length_of_stay')}
                            </th>
                            <th onClick={() => requestSort('total_earning')} style={{ cursor: 'pointer' }}>
                                Total Earning{getSortIndicator('total_earning')}
                            </th>
                            <th onClick={() => requestSort('avg_earning')} style={{ cursor: 'pointer' }}>
                                Avg. Earning{getSortIndicator('avg_earning')}
                            </th>
                            <th onClick={() => requestSort('percentage')} style={{ cursor: 'pointer' }}>
                                % of Total{getSortIndicator('percentage')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Gunakan data yang sudah diurutkan (sortedData) */}
                        {sortedData.map((row) => (
                            <tr key={row.name}>
                                <td>{row.name}</td>
                                <td>{row.nb_reservations}</td>
                                <td>{row.nb_nights}</td>
                                <td>{row.avg_length_of_stay}</td>
                                <td>{formatPrice(Number(row.total_earning || 0))}</td>
                                <td>{formatPrice(Number(row.avg_earning || 0))}</td>
                                <td>{row.percentage}%</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="fw-bold">
                            <td>Total</td>
                            <td>{total.nb_reservations}</td>
                            <td>{total.nb_nights}</td>
                            <td>{total.avg_length_of_stay}</td>
                            <td>{formatPrice(Number(total.total_earning || 0))}</td>
                            <td>{formatPrice(Number(total.avg_earning || 0))}</td>
                            <td>{total.percentage}%</td>
                        </tr>
                    </tfoot>
                </Table>
            </Card.Body>
        </Card>
    );
};

export default ReservationTableV4Component;